/**
 * Spanish (Español) Language File
 * 
 * This file contains all Spanish translations for the application.
 */

export const es = {
  // Language info
  language: {
    code: 'es',
    name: 'Español',
    flag: '🇪🇸',
  },

  // Common/General translations
  common: {
    welcome: 'Bienvenidos',
    back: 'Volver',
    backTo: 'Volver a',
    next: 'Siguiente',
    previous: 'Anterior',
    continue: 'Continuar',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    save: 'Guardar',
    delete: 'Eliminar',
    edit: 'Editar',
    close: 'Cerrar',
    loading: 'Cargando',
    error: 'Error',
    success: 'Éxito',
    warning: 'Advertencia',
    info: 'Información',
    yes: 'Sí',
    no: 'No',
    ok: 'OK',
  },

  // Home Page
  home: {
    title: 'Bienvenidos',
    subtitle: 'Elige un juego para jugar:',
    chooseGame: 'Elige un juego',
    heroSubtitle: 'Juegos interactivos que fortalecen la fe y conectan comunidades',
    exploreGames: 'Explorar Juegos',
    ourGames: 'Nuestros Juegos',
    conectadosDescription: 'Explora categorías de Sabiduría y Alianza con preguntas diseñadas para profundizar tu conocimiento.',
    playGame: 'Jugar',
    followUs: 'Conectate con nosotros',
    copyright: '© 2025 Fe en Juego. Todos los derechos reservados.',
  },

  // Resources Section
  resources: {
    title: 'Recursos para Culto Joven',
    subtitle: 'Materiales y recursos descargables para líderes y equipos',
    download: 'Descargar',
    downloadLabel: 'Descargar',
    comingSoon: 'Próximamente',

    // Placeholder resources
    placeholders: {
      guides: {
        title: 'Guías de Juego',
        description: 'Instrucciones completas y guías paso a paso para facilitar el juego en tu grupo.',
      },
      presentations: {
        title: 'Presentaciones PPT',
        description: 'Presentaciones listas para usar en tus reuniones y actividades grupales.',
      },
      additionalMaterial: {
        title: 'Material Adicional',
        description: 'PDFs, hojas de trabajo y otros recursos complementarios para enriquecer la experiencia.',
      },
    },
  },

  // Links of Interest Section
  links: {
    title: 'Enlaces de Interés',
    subtitle: 'Recursos y sitios web útiles para la comunidad adventista',
    visit: 'Visitar',
    visitLabel: 'Visitar sitio web',

    // Individual links
    items: {
      iasd: {
        title: 'Página IASD',
        description: 'Sitio oficial de la Iglesia Adventista del Séptimo Día',
      },
      noticiasIasd: {
        title: 'Noticias IASD',
        description: 'Portal de noticias adventistas',
      },
      whatsappUu: {
        title: 'Canal WhatsApp UU',
        description: 'Canal oficial de WhatsApp Unión Uruguaya',
      },
      feliz7play: {
        title: 'Feliz7Play',
        description: 'Plataforma de contenido audiovisual adventista',
      },
      videosIasd: {
        title: 'Videos IASD',
        description: 'Canal oficial de YouTube de IASD Sudamérica',
      },
      radioNt: {
        title: 'Nuevo Tiempo UY',
        description: 'Radio Nuevo Tiempo Uruguay en vivo',
      },
      materialesIasd: {
        title: 'Materiales IASD',
        description: 'Centro de descargas de materiales adventistas',
      },
      paraElSabado: {
        title: 'Para el Sábado',
        description: 'Materiales especiales para tus actividades sabáticas',
      },
      galeriaImagenes: {
        title: 'Galería de Imágenes',
        description: 'Galería de imágenes cristianas para uso ministerial',
      },
      leccionJovenes: {
        title: 'Lección Jóvenes',
        description: 'Lecciones y materiales para clases de jóvenes',
      },
      jaMateriales: {
        title: 'Materiales JA',
        description: 'Materiales e información para Jóvenes Adventistas',
      },
      accionJoven: {
        title: 'Revista Acción Joven',
        description: 'Revista trimestral para jóvenes adventistas',
      },
      conquistadores: {
        title: 'Conquistadores',
        description: 'Materiales e información del club de Conquistadores',
      },
      ministerioNino: {
        title: 'Ministerio del Niño',
        description: 'Recursos para el ministerio infantil',
      },
      recursosBiblicos: {
        title: 'Recursos Bíblicos',
        description: 'Materiales y recursos para el estudio de la Biblia',
      },
      meditaciones2025: {
        title: 'Meditaciones 2025',
        description: 'Meditaciones para la puesta de sol 2025',
      },
      seme: {
        title: '7me',
        description: 'Aplicación oficial para miembros y amigos Adventistas',
      },
      probadVed: {
        title: 'Probad y Ved',
        description: 'Videos sobre mayordomía cristiana',
      },
    },
  },

  // Footer
  footer: {
    copyright: '© 2026 Fe en Juego. Todos los derechos reservados.',
  },

  // Conecta2 Game
  conectados: {
    // Main page
    title: 'Conecta2',
    playing: 'Estás jugando',
    subtitle: 'Juego de preguntas y respuestas',
    selectCategory: 'Selecciona una categoría',

    // Access control
    access: {
      title: 'Acceso a Conecta2',
      message: 'Ingresá el código que viene con tu juego.',
      placeholder: 'XXXXXX',
      submit: 'Acceder',
      invalidCode: 'Código de acceso incorrecto. Por favor, verifica el código en las instrucciones del juego.',
      emptyCode: 'Por favor, ingresa un código de acceso.',
      success: '¡Acceso concedido! Redirigiendo...',
      infoTitle: '¿Dónde encuentro el código?',
      infoText: 'El código de acceso se encuentra en las instrucciones incluidas en el juego.',
    },

    // Game controls
    startGame: 'Iniciar Juego',
    endGame: 'Finalizar Partida',
    resetGame: 'Reiniciar Juego',
    newGame: 'Nuevo Juego',

    // Categories
    categories: {
      sabiduria: {
        title: 'Sabiduría',
        description: 'Pon a prueba tu conocimiento en diferentes áreas',
        alt: 'Sabiduría - Pon a prueba tu conocimiento de lugares, variedades y personajes',
      },
      alianza: {
        title: 'Alianza',
        description: 'Explora el pacto, la unidad y la valentía',
        alt: 'Alianza - Explora temas de pacto, unidad y valentía',
      },
    },

    // Subcategories
    subcategories: {
      // Sabiduria
      lugares: {
        title: 'Lugares',
        description: 'Lugares y ubicaciones bíblicas',
      },
      variedades: {
        title: 'Variedades',
        description: 'Diferentes aspectos de la fe',
      },
      personajes: {
        title: 'Personajes',
        description: 'Personajes y figuras bíblicas',
      },

      // Alianza
      'si-nosotros': {
        title: 'Si Nosotros',
        description: 'Nuestro papel en el pacto de Dios',
      },
      coincidimos: {
        title: 'Coincidimos',
        description: 'Encontrar puntos en común en la fe',
      },
      arriesgamos: {
        title: 'Arriesgamos',
        description: 'Tomar riesgos por nuestras creencias',
      },
    },

    // Question page
    question: {
      title: 'Pregunta',
      question: 'Pregunta',
      prompt: 'Consigna',
      answer: 'Respuesta',
      options: 'Opciones de respuesta:',
      whichVerse: '¿Qué versículo utilizarías?',
      reference: 'Referencia bíblica:',
      showAnswer: 'Mostrar Respuesta',
      hideAnswer: 'Ocultar Respuesta',
      endTurn: 'Terminar Turno',
      nextQuestion: 'Siguiente Pregunta',
      loading: 'Cargando pregunta...',
      noQuestions: 'No hay más preguntas disponibles',
      error: 'Error al cargar preguntas',
      tryAgain: 'Intentar de Nuevo',
      trueOption: 'Verdadero',
      falseOption: 'Falso',
    },

    // Stats
    stats: {
      questionsAnswered: 'Preguntas respondidas:',
      totalQuestions: 'Total de preguntas:',
      questionsRemaining: 'Preguntas restantes:',
      resetQuestions: 'Reiniciar Preguntas',
      categoryStats: 'Estadísticas de esta subcategoría',
    },

    // Completion
    completion: {
      congratulations: '¡Felicitaciones!',
      allQuestionsCompleted: '¡Has respondido todas las preguntas de esta categoría!',
      questionsCompleted: 'Preguntas completadas:',
      returnToConectados: 'Volver a Conecta2',
      returnToCategories: 'Volver a Categorías',
    },

    // Footer
    footer: {
      quote: '"Es mejor ser dos que uno, porque ambos pueden ayudarse mutuamente a lograr el éxito."',
      reference: 'Eclesiastés 4:9 (NTV)',
    },

    // PDF Modal (How to Play)
    pdfModal: {
      title: 'Cómo Jugar',
      mobileTitle: 'Ver Instrucciones PDF',
      mobileDescription: 'Para una mejor experiencia en dispositivos móviles, puedes abrir el PDF en una nueva pestaña o descargarlo.',
      openPDF: 'Abrir PDF',
      downloadPDF: 'Descargar PDF',
    },
  },

  // Error messages
  errors: {
    general: 'Ha ocurrido un error',
    notFound: 'No encontrado',
    categoryNotFound: 'Categoría no encontrada',
    subcategoryNotFound: 'Subcategoría no encontrada',
    questionsNotFound: 'No se encontraron preguntas',
    loadingFailed: 'Error al cargar',
    networkError: 'Error de conexión',
  },

  // Success messages
  success: {
    saved: 'Guardado exitosamente',
    deleted: 'Eliminado exitosamente',
    updated: 'Actualizado exitosamente',
  },

  // Time and dates
  time: {
    seconds: 'segundos',
    minutes: 'minutos',
    hours: 'horas',
    days: 'días',
    weeks: 'semanas',
    months: 'meses',
    years: 'años',
  },

  // Accessibility
  a11y: {
    menu: 'Menú',
    close: 'Cerrar',
    open: 'Abrir',
    previous: 'Anterior',
    next: 'Siguiente',
    loading: 'Cargando',
  },

  // Thank You / Payment Confirmation Page
  gracias: {
    // Success state
    success: {
      title: '¡Gracias por tu compra!',
      message: 'Tu pago fue recibido correctamente. Envianos el comprobante por WhatsApp junto con:',
      info1: 'Nombre completo',
      info2: 'Dirección',
      info3: 'Teléfono',
      info4: 'Forma de entrega (retiro o envío)',
      paymentCode: 'Código de pago:',
      whatsappButton: 'Enviar por WhatsApp',
    },

    // Pending state
    pending: {
      title: 'Tu pago está pendiente',
      message: 'Mercado Pago está revisando tu transacción.',
      submessage: 'Avisanos por WhatsApp cuando se confirme el pago.',
      paymentCode: 'Código de transacción:',
    },

    // Failure state
    failure: {
      title: 'El pago no se pudo completar',
      message: 'Hubo un problema procesando tu compra.',
      submessage: 'Podés intentar nuevamente o contactarnos por WhatsApp si necesitás ayuda.',
      retryButton: 'Reintentar pago',
      contactButton: 'Contactar por WhatsApp',
    },

    // Waiting state (no params)
    waiting: {
      title: 'Esperando información',
      message: 'Confirmá tu pago por Mercado Pago.',
      submessage: 'Avisanos por WhatsApp cuando se confirme el pago.',
    },
  },

  // Conecta2 Product Landing Page
  conecta2Product: {
    // SEO & Meta
    meta: {
      title: 'Conecta2 — Juego de mesa cristiano | Fe en Juego',
      description: 'Conecta2: juego de mesa cristiano uruguayo. Ideal para familias, jóvenes e iglesias. Precio lanzamiento y reservas por WhatsApp.',
    },

    // Hero Section
    hero: {
      launchBadge: 'Precio lanzamiento',
      title: '¡Lanzamiento oficial de Conecta2!',
      subtitle: 'Juego de mesa cristiano para familias, jóvenes e iglesias.',
      imageAlt: 'Caja Conecta2 sobre mesa',
    },

    // Price Section
    price: {
      title: 'Precio',
      subtitle: 'Elegí tu método de pago preferido',
      mercadopago: {
        label: 'MercadoPago',
        price: 940,
        detail: 'Hasta 12 cuotas',
      },
      transferencia: {
        label: 'Transferencia / Efectivo',
        price: 850,
        originalPrice: 940,
        discount: '10% OFF',
        detail: 'Pago único',
      },
      selected: 'Seleccionado',
      reserveButton: 'Reservar por WhatsApp',
      paymentInfoLink: 'Consultar métodos de pago y datos de cuenta por WhatsApp',
      whatsappMessage: {
        reserve: `Hola! Quiero reservar Conecta2.
Cantidad: 
Pago: {paymentMethod}
Nombre: 
Dirección (si envío): 
¿Tienen unidades disponibles?`,
        paymentInfo: `Hola! Me gustaría conocer:
• Todos los métodos de pago disponibles
• Datos de cuenta para transferencia
• Información de MercadoPago

¡Gracias!`,
      },
    },

    // What's Included Section
    includes: {
      title: '¿Qué incluye Conecta2?',
      items: [
        'Tablero',
        'Fichas para hasta 10 jugadores',
        'Talonario "Conexión"',
        'Instrucciones',
        'Acceso online a preguntas y desafíos',
      ],
    },

    // How to Play Section
    howToPlay: {
      title: 'Cómo jugar (en 3 pasos)',
      steps: [
        'Elegí tu ficha y ubicala en inicio.',
        'El jugador elige un conectado y resuelven la actividad.',
        'El primero que llegue a la casilla final y resuelva, gana.',
      ],
    },

    // Gallery Section
    gallery: {
      title: 'Galería de fotos',
      images: [
        { alt: 'Juego completo Conecta2' },
        { alt: 'Tablero y caja de Conecta2' },
        { alt: 'App con preguntas y desafíos' },
        { alt: 'Familia jugando Conecta2' },
        { alt: 'Detalle de las fichas del juego' },
        { alt: 'Talonario de conexión' },
        { alt: 'Instrucciones del juego' },
        { alt: 'Jugadores en acción' },
        { alt: 'Componentes del juego' },
      ],
    },

    // FAQ Section
    faq: {
      title: 'Preguntas frecuentes',
      items: [
        {
          question: '¿Cómo pago?',
          answer: 'Coordinamos por WhatsApp. Aceptamos transferencias, efectivo y MercadoPago (hasta 12 cuotas).',
        },
        {
          question: '¿Puedo comprar al por mayor?',
          answer: 'Sí, escribinos por WhatsApp para ver precios por cantidad.',
        },
        {
          question: '¿Hacen envíos?',
          answer: 'Sí, enviamos a todo el país mediante agencias de encomienda.',
        },
        {
          question: '¿Cuánto cuesta el envío?',
          answer: 'El costo depende de la localidad y del método de envío elegido. 💵 El cliente abona el envío al recibir o retirar el paquete.',
        },
        {
          question: '¿Cómo se coordina el envío?',
          answer: 'Luego de confirmar la compra, coordinamos todo por WhatsApp (método de envío, agencia, horarios, etc.) para que te llegue de la forma más rápida y cómoda posible.',
        },
      ],
    },

    // Testimonial Section
    testimonial: {
      title: 'Fe en Juego',
      text: 'Juegos que unen la fe y la familia',
      quote: '"Lámpara es a mis pies tu palabra..." — Salmo 119:105',
    },
  },
};

export default es;
